import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { ValidationService } from '../../services/validation.service';
import { GlobalService } from '../../services/global.service';
import { LoginService } from '../../services/login.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { ButtonComponent } from '../../components/button/button.component';
import { UserService } from '../../services/user.service';
import { passwordMatchValidator } from '../../common/password-match-validator';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgIf, TextInputComponent, ModalComponent, ButtonComponent],
  templateUrl: './login-page.component.html'
})
export class LoginPageComponent implements OnInit, OnDestroy {

  loginForm!: FormGroup;
  forgotForm!: FormGroup;
  reset_form!: FormGroup;
  user_type = 'staff';
  password_type: 'password' | 'text' = 'password';
  mode: 'login' | 'forgot' = 'login';
  forgotStep: 1 | 2 | 3 = 1;
  otpBoxes = new Array(6).fill(0);
  otpValues: string[] = ['', '', '', '', '', ''];
  otpTimer = 300; // 5 minutes = 300 sec
  otpInterval: any;
  canResendOtp = false;
  otpError = '';
  params: any = {};



  @ViewChild('form_modal') form_modal: ModalComponent | undefined;

  private route_sub!: Subscription;

  constructor(private fb: FormBuilder, private route: ActivatedRoute, private router: Router, public vs: ValidationService, public gs: GlobalService, public login: LoginService, public us: UserService) { }

  ngOnInit(): void {
    this.route_sub = this.route.queryParams.subscribe(params => {
      this.user_type = params['user_type'] || 'staff';
      this.params = { ...params };
      this.createForms();
      this.setLoginValidations();
    });
  }

  createForms() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.vs.email_pattern), Validators.maxLength(this.vs.max_length_email)]],
      password: ['', [Validators.required]],
      roll_number: ''
    });

    this.forgotForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.vs.email_pattern), Validators.maxLength(this.vs.max_length_email)]],
      otp: [''],
      roll_number: '',
      Password: [''],
      new_password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(this.vs.password_complex_pattern)]],
      confirm_password: ['', [Validators.required]]
    }, { validators: passwordMatchValidator });

    this.reset_form = this.fb.group({
      email: [this.loginForm.value.email],
      new_password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(this.vs.password_complex_pattern)]],
      confirm_password: ['', [Validators.required]],
    }, { validators: passwordMatchValidator });
  }

  toggle_password() {
    this.password_type =
      this.password_type === 'password' ? 'text' : 'password';
  }

  setLoginValidations() {
    const emailCtrl = this.loginForm.get('email');
    const rollCtrl = this.loginForm.get('roll_number');

    if (this.user_type === 'student') {
      rollCtrl?.setValidators([Validators.required]);
      emailCtrl?.clearValidators();
      emailCtrl?.reset();
    } else {
      emailCtrl?.setValidators([
        Validators.required,
        Validators.pattern(this.vs.email_pattern),
        Validators.maxLength(this.vs.max_length_email)
      ]);
      rollCtrl?.clearValidators();
      rollCtrl?.reset();
    }

    emailCtrl?.updateValueAndValidity();
    rollCtrl?.updateValueAndValidity();
  }


  // ================= LOGIN =================
  async submit() {
    try {
      if (!this.loginForm.valid) return;
      const user_type = this.params?.user_type ?? 'staff';
      let payload: any = { password: this.loginForm.value.password };

      if (user_type === 'student') {
        payload.roll_number = this.loginForm.value.roll_number;
      } else {
        payload.email = this.loginForm.value.email;
      }
      const create_response = await this.login.login_user(payload);
      if (create_response.data.first_login == true) {
        this.form_modal?.open();
        return;
      }
      this.gs.user_data = create_response.data;
      localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
      const role_data = await this.us.get(create_response.data.id);
      this.gs.user_role_data = user_type == 'staff' ? role_data.data.Role : role_data.data?.user?.Role;
      localStorage.setItem('user_role', JSON.stringify(this.gs.user_role_data));
      this.gs.check_and_fetch_from_local_storage()
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      if (this.gs.user_data.first_login === true && this.params.user_type === 'staff') {
        this.form_modal?.open();
      } else {
        const landing_page = this.gs.user_role_data?.landing_page || '/dashboard';
        this.router.navigate([landing_page]);
      }
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }


  openForgotPassword() {
    this.mode = 'forgot';
    this.forgotStep = 1;
  }

  // STEP 1 : SEND OTP
  async sendOtp() {
    try {
      let payload = { email: this.forgotForm.value.email }
      let create_response = await this.login.send_otp(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      this.forgotStep = 2;
      this.startOtpTimer();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }

  }

  // STEP 2 : VERIFY OTP
  async verifyOtp() {
    try {
      this.otpError = '';
      const otp = this.otpValues.join('');
      if (!otp) {
        this.otpError = 'OTP is required';
        return;
      }

      if (otp.length !== 6) {
        this.otpError = 'Please enter 6 digit OTP';
        return;
      }

      if (this.otpTimer === 0) {
        this.otpError = 'OTP expired. Please resend OTP';
        return;
      }

      const payload = {
        email: this.forgotForm.value.email,
        otp: otp
      };

      let res = await this.login.verify_otp(payload);
      this.gs.toastr_shows_function(res.message, 'Success', 'success');

      this.forgotStep = 3;

    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }



  // STEP 3 : befor login RESET PASSWORD
  async resetPassword() {
    try {
      if (this.forgotForm.invalid) {
        this.forgotForm.markAllAsTouched();
        return;
      }
      if (this.forgotForm.hasError('passwordMismatch')) {
        this.gs.toastr_shows_function('Passwords do not match', 'Error', 'error');
        return;
      }
      let payload = { email: this.forgotForm.value.email, new_password: this.forgotForm.value.new_password }
      let create_response = await this.login.update_password(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      this.backToLogin();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  // after login reset
  async reset() {
    if (this.reset_form.invalid) {
      this.reset_form.markAllAsTouched();
      return;
    }
    if (this.reset_form.hasError('passwordMismatch')) {
      this.gs.toastr_shows_function('Passwords do not match', 'Error', 'error');
      return;
    }
    try {
      let payload = {
        email: this.loginForm.value.email,
        // token: this.gs?.user_data?.token,
        new_password: this.reset_form.value.new_password,
        user_type: this.params.user_type ? this.params.user_type : 'staff'
      }
      let create_response = await this.login.update_password(payload)
      this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      // this.forgotStep = 1;
      // this.backToLogin();
      this.form_modal?.close();
      this.router.navigate(['/sign-in']);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  backToLogin() {
    console.log('BACK TO LOGIN');
    this.mode = 'login';

    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
  }

  ngOnDestroy(): void {
    this.route_sub?.unsubscribe();
  }

  onOtpInput(event: any, index: number) {
    const value = event.target.value;

    if (!/^\d$/.test(value)) {
      event.target.value = '';
      return;
    }

    this.otpValues[index] = value;

    // Auto focus next box
    if (index < 5) {
      const next = event.target.nextElementSibling;
      next?.focus();
    }
  }
  onOtpKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpValues[index] && index > 0) {
      const prev = (event.target as HTMLElement).previousElementSibling as HTMLElement;
      prev?.focus();
    }
  }


  startOtpTimer() {
    this.otpTimer = 300;
    this.canResendOtp = false;

    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }

    this.otpInterval = setInterval(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.canResendOtp = true;
        clearInterval(this.otpInterval);
      }
    }, 1000);
  }
  get otpTime() {
    const min = Math.floor(this.otpTimer / 60);
    const sec = this.otpTimer % 60;
    return `${min}:${sec < 10 ? '0' + sec : sec}`;
  }
  async resendOtp() {
    if (!this.canResendOtp) return;

    try {
      let payload = { email: this.forgotForm.value.email, user_type: this.params.user_type ? this.params.user_type : 'staff' };

      let create_response = await this.login.send_otp(payload);
      this.gs.toastr_shows_function('OTP resent successfully', 'Success', 'success');

      // Reset OTP boxes
      this.otpValues = ['', '', '', '', '', ''];

      this.startOtpTimer(); // 🔄 Restart timer
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  close_form_modal(): void {
    this.form_modal?.close();
  }

}